<script setup lang="js">
/**
 Documentation: https://vue3-datatable-document.vercel.app/

 Columns Definition:
 	https://vue3-datatable-document.vercel.app/#column-props

 **/

import { computed, onBeforeMount, onMounted, ref } from 'vue'
import Vue3Datatable from "@comagine/vue3-datatable"
import "@comagine/vue3-datatable/dist/style.css"
import { api } from '@/api/ApiGateway.js'
import { Toaster } from '@/models/utils/Toaster.js'
import Combobox from '@/components/Forms/Combobox.vue'

const isLoading = ref(false)
const totalRows = ref(0)
const searchBarModel = ref('')
const tableSearchValue = ref('')
const currentPage = ref(1)
const pageSize = ref(10) // Currently the max page size, which is enforced by the ComagineApi, is 20
const sortColumn = ref('')
const sortDirection = ref('asc')
const toaster = new Toaster()

const props = defineProps({
	/**
	 * An array of {@link DataTableEndpointDefinition} objects. An array of at least 1 object is required.
	 */
	endpoints: { type: Array, default: () => []},
	initialPageSize: {
		type: Number,
		default: 10,
		validator(value) {
			return value <= 20 // Currently the max page size, which is enforced by the ComagineApi, is 20
		}
	},
	stickyHeader: { type: Boolean, default: true },
	showFirstPage: { type: Boolean, default: false },
	showLastPage: { type: Boolean, default: false },
	/**
	 * Whether to use the endpoint or provided data
	 */
	serverMode: { type: Boolean, default: true },
	/**
	 * If provided, this data will be used rather than fetching data from an endpoint.
	 * {@link serverMode} must be 'false' for this data to be used.
	 */
	rowData: { type: Array, default: () => [] },
	forcePagination: { type: Boolean, default: false },

	/**
	 * Function that when returns false, prevents a row checkbox from being selected.
	 */
	enableRowSelectFn: { type: Function, default: () =>
		{
			return true
		}}
})

const table = ref(null)
const rows = ref(!props.serverMode ? props.rowData : [])
const emit = defineEmits(['onDataLoaded', 'rowSelected', 'serviceSelected', 'selectAll'])
const showPagination = ref(true)
const isSortingEnabled = computed(() => {
	return selectedService.value?.enableSorting && (!props.serverMode || totalRows.value > 0)
})
const selectedService = ref(null)
const serviceNames = ref([])
const selectedServiceName = ref(null)
const enableSearch = ref(false)


onBeforeMount(() => {
	setup(props.endpoints[0])
})

onMounted(() => {
	if (props.serverMode) {
		loadData()
	}
	else {
		showPagination.value = props.totalRows > 10
	}
})

function selectAll(rows)
{
	emit('selectAll', rows)
}

function setup(service) {
	selectedService.value = service
	serviceNames.value = props.endpoints.map(endpoint => endpoint.endpointDisplayName)
	selectedServiceName.value = selectedService.value.endpointDisplayName
	enableSearch.value = selectedService.value.enableSearch

	pageSize.value = props.initialPageSize
	sortColumn.value = selectedService.value.initialSortColumn
	sortDirection.value = selectedService.value.initialSortDirection
}

function loadData() {
	isLoading.value = true
	tableSearchValue.value = searchBarModel.value//Updates the search if the user checks an adv checkbox after typing

	const defaultParams = {
		"page": currentPage.value,
		"limit": pageSize.value,
		"sort": sortDirection.value === 'asc' ? sortColumn.value : '-' + sortColumn.value,
		"searchValue": tableSearchValue.value
	}

	let filters
	if (tableSearchValue.value) {
		if (selectedService.value.fullTextSearch) {
			filters = {
				'filters[fulltext_search]': tableSearchValue.value
			}
		}
		else {
			filters = selectedService.value.columns.reduce((accumulator, column) => {
				if (column.search) {
					accumulator[`filters[${column.searchField ?? column.field}][infix]`] = tableSearchValue.value
				}

				return accumulator
			}, {})
		}
	}

	const advancedSearchParams = selectedService.value?.advancedSearchParams?.reduce((accumulator, param, index) => {
		accumulator[`advSearches[${index}][id]`] = param.id
		accumulator[`advSearches[${index}][value]`] = param.value
		return accumulator
	}, {})

	const params = { ...defaultParams, ...selectedService.value.endpointParams, ...advancedSearchParams, ...filters }

	api.get(selectedService.value.endpointRoute, params).then(response =>
	{
		if (response.hasError() || !response.body()?.data)
		{
			toaster.error('There was an issue loading data.')
			isLoading.value = false
			return
		}

		const responseBody = response.body()
		rows.value = responseBody.data
		totalRows.value = responseBody.meta.total

		showPagination.value = totalRows.value > 10

		emit('onDataLoaded', responseBody)

		isLoading.value = false
	})
}

const changePage = (page) => {
	currentPage.value = page
	loadData()
}

const changePageSize = (size) => {
	pageSize.value = size
	currentPage.value = 1
	loadData()
}

const sortChange = (sort) => {
	if (sortColumn.value === sort.field && sortDirection.value === 'desc') {
		// Sorting the same column as before. If it was sorted as desc, switch it to no sort/default
		sortColumn.value = selectedService.value.initialSortColumn
		sortDirection.value = selectedService.value.initialSortDirection
		table.value.reset()
	}
	else {
		sortColumn.value = sort.field
		sortDirection.value = sort.direction
	}

	if (props.serverMode) {
		loadData()
	}
}

const searchChange = (newValue) => {
	tableSearchValue.value = newValue
}

const rowSelected = (rows, row, index) => {
	emit('rowSelected', rows, row, index)
}

function searchKeyDown(event) {
	if (event && event.keyCode === 13) // 13 == Enter
	{
		currentPage.value = 1
		tableSearchValue.value = searchBarModel.value
		loadData()
	}
}

function clearSearchBar()
{
	currentPage.value = 1
	tableSearchValue.value = ''
	searchBarModel.value = ''
	loadData()
}

function serviceSelected(serviceName) {
	const newService = props.endpoints.find(endpoint => endpoint.endpointDisplayName === serviceName)
	setup(newService)
	clearSearchBar()
	emit('serviceSelected')
}

function selectRow(p_index)
{
	table.value.selectRow(p_index)
}

function unselectRow(p_index)
{
	table.value.unselectRow(p_index)
}

function isRowSelected(p_index)
{
	return table.value.isRowSelected(p_index)
}

function clearSelectedRows()
{
	table.value.clearSelectedRows()
}

function getSelectedRows()
{
	return table.value.getSelectedRows()
}

function rowCount()
{
	return totalRows.value;
}

function getRows()
{
	return rows.value;
}

defineExpose({
	loadData, selectRow, unselectRow, isRowSelected, clearSelectedRows, rowCount, getSelectedRows, getRows
})
</script>

<template>
	<div class="flex w-full space-x-4">
		<Combobox v-if="endpoints.length > 1" id="combo_services" v-model="selectedServiceName" :items="serviceNames" title="" placeholder="Select service"
		          input-height="h-10" :readonly="true" :disabled="isLoading" class="z-99 w-50" @onChange="serviceSelected" />
		<div v-if="enableSearch" class="flex-grow group">
			<div class="h-10 flex flex-row px-3 rounded border border-neutral group-focus-within:ring-1 group-focus-within:ring-neutral-subtle group-focus-within:bg-neutral group-focus-within:border-neutral-subtle
										group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover">
				<input
					type="text"
					name="search"
					autocomplete="off"
					data-lpignore="true"
					:placeholder="selectedService.searchPlaceholder"
					v-model="searchBarModel"
					class="userpilot-mask w-full placeholder-default border-none p-0 border-transparent focus:border-transparent focus:ring-0"
					@keydown="searchKeyDown"
				/>
				<button v-show="searchBarModel && searchBarModel !== ''" @click.prevent="clearSearchBar" aria-label="Clear Search">
					<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" >
						<title>Clear search bar contents</title>
						<g clip-path="url(#clip0_6034_10634)">
							<path d="M8.7999 8.00039L14.8999 1.90039C15.1249 1.67539 15.1249 1.32539 14.8999 1.10039C14.6749 0.875391 14.3249 0.875391 14.0999 1.10039L7.9999 7.20039L1.8999 1.10039C1.6749 0.875391 1.3249 0.875391 1.0999 1.10039C0.874902 1.32539 0.874902 1.67539 1.0999 1.90039L7.1999 8.00039L1.0999 14.1004C0.874902 14.3254 0.874902 14.6754 1.0999 14.9004C1.1999 15.0004 1.3499 15.0754 1.4999 15.0754C1.6499 15.0754 1.7999 15.0254 1.8999 14.9004L7.9999 8.80039L14.0999 14.9004C14.1999 15.0004 14.3499 15.0754 14.4999 15.0754C14.6499 15.0754 14.7999 15.0254 14.8999 14.9004C15.1249 14.6754 15.1249 14.3254 14.8999 14.1004L8.7999 8.00039Z" class="fill-icon-primary"/>
						</g>
						<defs>
							<clipPath id="clip0_6034_10634">
								<rect width="16" height="16" fill="white"/>
							</clipPath>
						</defs>
					</svg>
				</button>
			</div>
		</div>
	</div>

	<slot v-if="enableSearch && selectedService.advancedSearchParams" name="advanced-search"></slot>

	<div :class="[{ 'is-short-table': totalRows <= pageSize }, { 'mt-3': enableSearch }, { 'mt-4': !enableSearch }]">
		<slot name="table-menu"></slot>
		<vue3-datatable
			ref="table"
			:rows="rows"
			:columns="selectedService.columns"
			:loading="isLoading"
			:totalRows="totalRows"
			:isServerMode="serverMode"
			:pageSize="pageSize"
			:sortable="isSortingEnabled"
			:sortColumn="sortColumn"
			:sortDirection="sortDirection"
			:search="tableSearchValue"
			:stickyHeader="stickyHeader"
			:showFirstPage="showFirstPage"
			:showLastPage="showLastPage"
			:hasCheckbox="selectedService.showCheckboxes"
			:enableRowSelectFn="enableRowSelectFn"
			:pagination="forcePagination || showPagination"
			:pageSizeOptions="[10, 20]"
			skin="bh-table-striped bh-table-bordered"
			paginationInfo="Showing {0} to {1} of {2}"
			@pageChange="changePage"
			@pageSizeChange="changePageSize"
			@sortChange="sortChange"
			@searchChange="searchChange"
			@rowSelect="rowSelected"
			@selectAll="selectAll"
		>
			<template v-for="(_, slotName) in $slots" v-slot:[slotName]="slotProps">
				<slot :name="slotName" v-bind="slotProps" />
			</template>
		</vue3-datatable>
	</div>
</template>

<style scoped>
	:deep(select) {
		@apply pr-8 !important;
	}

	:deep(.bh-table-responsive) {
		@apply h-fit !important;
	}

	/* Entire table */
	:deep(.bh-datatable table) {
		@apply text-body text-[16px] leading-6 font-normal;
		@apply border-2 border-primary-subtle;
	}

	/* Header row */
	:deep(.bh-table-responsive table thead tr th) {
		@apply bg-primary-lightest;
	}

	/* Header container */
	:deep(.bh-table-responsive table thead) {
		/* Top offset -1px to cover gap due to sticky header */
		top: -1px;
	}

	/* Even rows */
	:deep(.bh-table-responsive table tbody tr:nth-child(even)) {
		@apply bg-primary-subtle;
		/*@apply hover:bg-primary hover:bg-opacity-10;*/
	}

	/* Odd rows */
	:deep(.bh-table-responsive table tbody tr:nth-child(odd)) {
		@apply bg-white;
		/*@apply hover:bg-primary hover:bg-opacity-10;*/
	}

	/* Even row cells */
	:deep(.bh-table-responsive table.bh-table-bordered tbody tr:nth-child(even) td, .bh-table-responsive table.bh-table-bordered thead tr:nth-child(even) th) {
		@apply border-r-primary-subtle;
	}

	/* Odd row cells */
	:deep(.bh-table-responsive table.bh-table-bordered tbody tr:nth-child(odd) td, .bh-table-responsive table.bh-table-bordered thead tr:nth-child(odd) th) {
		@apply border-r-primary-subtle;
	}

	/* Active pagination button */
	:deep(.bh-pagination button.bh-active) {
		@apply bg-primary text-primary-on-color border-primary;
	}

	/* Disabled pagination buttons */
	:deep(.bh-pagination button) {
		@apply hover:bg-primary hover:text-primary-on-color hover:border-primary;
	}

	/* Active sort direction arrow */
	:deep(.bh-sort svg polygon.bh-text-black\/20.\!bh-text-primary),
	:deep(.bh-sort svg polygon.\!bh-text-primary) {
		@apply text-icon-neutral !important;
	}

	/* Checkbox */
	:deep(.bh-checkbox div) {
		@apply bg-primary border-2 border-primary rounded !important;
	}

	/* Checkbox unchecked */
	:deep(.bh-checkbox:not(:has(input:checked)) div) {
		@apply bg-white !important;
	}

	/* Checkbox check mark */
	:deep(.bh-checkbox .check) {
		@apply text-white;
	}

</style>